import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';

export const Route = createFileRoute('/admin/properties/')({
  component: AdminProperties,
});

type PropertySummary = {
  id: string;
  name: string;
  archivedAt: string | null;
  memberCount: number;
  owner: { id: string; name: string; email: string } | null;
};

function AdminProperties() {
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [cursor, setCursor] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const { data, isPending } = useQuery({
    queryKey: ['admin-properties', q, cursor, showArchived],
    queryFn: () => {
      const params = new URLSearchParams({ limit: '25' });
      if (q) params.set('q', q);
      if (cursor) params.set('cursor', cursor);
      if (showArchived) params.set('includeArchived', 'true');
      return api.get<{ properties: PropertySummary[]; nextCursor: string | null }>(
        `/v1/admin/properties?${params}`,
      );
    },
  });

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    setQ(search);
    setCursor(null);
  }

  return (
    <div className="max-w-4xl">
      <h1 className="text-xl font-semibold mb-6">Properties</h1>

      <form onSubmit={handleSearch} className="flex gap-2 mb-4">
        <input
          type="text"
          placeholder="Search by name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        />
        <Button type="submit">Search</Button>
        {q && (
          <Button
            variant="outline"
            type="button"
            onClick={() => { setSearch(''); setQ(''); setCursor(null); }}
          >
            Clear
          </Button>
        )}
      </form>

      <label className="flex items-center gap-1.5 text-sm cursor-pointer mb-4">
        <input
          type="checkbox"
          checked={showArchived}
          onChange={(e) => { setShowArchived(e.target.checked); setCursor(null); }}
        />
        Show archived
      </label>

      {isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="rounded-md border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Name</th>
                <th className="text-left px-4 py-2 font-medium">Owner</th>
                <th className="text-left px-4 py-2 font-medium">Members</th>
                <th className="text-left px-4 py-2 font-medium">Status</th>
                <th />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data?.properties.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-4 text-center text-muted-foreground">
                    No properties found.
                  </td>
                </tr>
              )}
              {data?.properties.map((p) => (
                <tr key={p.id} className="hover:bg-muted/40">
                  <td className="px-4 py-2 font-medium">{p.name}</td>
                  <td className="px-4 py-2 text-muted-foreground">{p.owner?.email ?? '—'}</td>
                  <td className="px-4 py-2">{p.memberCount}</td>
                  <td className="px-4 py-2">
                    {p.archivedAt ? (
                      <span className="text-xs text-muted-foreground">Archived</span>
                    ) : (
                      <span className="text-xs text-green-600">Active</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <Link
                      to="/admin/properties/$propertyId"
                      params={{ propertyId: p.id }}
                      className="text-primary hover:underline"
                    >
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(cursor !== null || data?.nextCursor) && (
        <div className="flex justify-between mt-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCursor(null)}
            disabled={cursor === null}
          >
            First page
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCursor(data?.nextCursor ?? null)}
            disabled={!data?.nextCursor}
          >
            Next →
          </Button>
        </div>
      )}
    </div>
  );
}
